import { useState, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Shield, Users, MapPin, Clock, MessageCircle, ChevronRight, Globe, AlertTriangle, User } from 'lucide-react';

const WHATSAPP_NUMBER = "+41779938989";

const serviceTypes = [
  { id: 'personal', label: 'Persönliche Sicherheit', icon: Shield },
  { id: 'event', label: 'Veranstaltung & Publikum', icon: Users },
  { id: 'travel', label: 'Reise ins Ausland', icon: Globe },
  { id: 'property', label: 'Objektschutz', icon: MapPin },
  { id: 'access', label: 'Zutritt & Empfang', icon: Clock },
];

const locationOptions = [
  { id: 'switzerland', label: 'Schweiz' },
  { id: 'germany', label: 'Deutschland' },
  { id: 'europe', label: 'Europa' },
  { id: 'international', label: 'International' },
];

const timeOptions = [
  { id: 'days', label: 'In den nächsten Tagen' },
  { id: 'weeks', label: 'In 1-4 Wochen' },
  { id: 'months', label: 'In 1-3 Monaten' },
  { id: 'ongoing', label: 'Langfristig / laufend' },
];

const threatOptions = [
  { id: 'acute', label: 'Ja, akut', icon: AlertTriangle },
  { id: 'suspected', label: 'Ja, vermutet', icon: AlertTriangle },
  { id: 'preventive', label: 'Nein, präventiv', icon: Shield },
  { id: 'private', label: 'Möchte ich nicht sagen', icon: User },
];

const personOptions = [
  { id: 'single', label: 'Einzelperson', icon: User },
  { id: 'small', label: 'Kleine Gruppe (2-5)', icon: Users },
  { id: 'large', label: 'Grössere Gruppe (5+)', icon: Users },
  { id: 'org', label: 'Organisation / Firma', icon: Shield },
];

export const SecurityCalculator = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const [selectedService, setSelectedService] = useState('personal');
  const [selectedLocation, setSelectedLocation] = useState('switzerland');
  const [selectedTime, setSelectedTime] = useState('days');
  const [selectedThreat, setSelectedThreat] = useState('preventive');
  const [selectedPersons, setSelectedPersons] = useState('single');

  const openWhatsApp = () => {
    const serviceLabel = serviceTypes.find(s => s.id === selectedService)?.label || '';
    const locationLabel = locationOptions.find(l => l.id === selectedLocation)?.label || '';
    const timeLabel = timeOptions.find(t => t.id === selectedTime)?.label || '';
    const threatLabel = threatOptions.find(t => t.id === selectedThreat)?.label || '';
    const personsLabel = personOptions.find(p => p.id === selectedPersons)?.label || '';

    const message = `Guten Tag,

ich interessiere mich für Ihre Dienstleistungen und habe folgende Konfiguration erstellt:

📋 Meine Auswahl:
• Bedarf: ${serviceLabel}
• Ort: ${locationLabel}
• Zeitraum: ${timeLabel}
• Bedrohung: ${threatLabel}
• Personen: ${personsLabel}

Ich freue mich auf Ihre Rückmeldung.`;

    window.open(`https://wa.me/${WHATSAPP_NUMBER.replace(/\+/g, '')}?text=${encodeURIComponent(message)}`, '_blank');
  };

  const isComplete = selectedService && selectedLocation && selectedTime && selectedThreat && selectedPersons;

  return (
    <section className="section-padding bg-[#0B0F19] relative overflow-hidden" data-testid="calculator-section">
      {/* Background Accent */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] rounded-full bg-[#D4AF37]/5 blur-3xl" />
      </div>

      <div className="container-custom relative">
        {/* Header */}
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <span className="text-[#D4AF37] uppercase tracking-[0.3em] text-xs font-medium">
            Massgeschneidert
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-semibold text-[#F1F5F9] mt-4 mb-6 font-['DM_Sans']">
            Anfrage konfigurieren
          </h2>
          <p className="text-[#94A3B8] max-w-2xl mx-auto">
            Stellen Sie Ihre Wünsche zusammen. Wir melden uns umgehend mit einem Vorschlag.
          </p>
        </motion.div>

        {/* Calculator Card */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="max-w-4xl mx-auto bg-[#111625] border border-[#1E293B] rounded-sm p-6 md:p-10"
        >
          {/* Step 1: Service Type */}
          <div className="mb-10">
            <div className="flex items-center gap-3 mb-4">
              <span className="w-8 h-8 rounded-full bg-[#D4AF37] text-[#0B0F19] flex items-center justify-center text-sm font-bold">1</span>
              <h3 className="text-lg font-semibold text-[#F1F5F9] font-['DM_Sans']">
                Worum geht es?
              </h3>
            </div>
            <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3">
              {serviceTypes.map((service) => {
                const Icon = service.icon;
                return (
                  <button
                    key={service.id}
                    onClick={() => setSelectedService(service.id)}
                    className={`p-4 rounded-sm border transition-all text-left flex items-center gap-4 ${selectedService === service.id
                      ? 'bg-[#D4AF37]/10 border-[#D4AF37] text-[#F1F5F9]'
                      : 'bg-[#0B0F19] border-[#1E293B] text-[#94A3B8] hover:border-[#D4AF37]/50'
                      }`}
                    data-testid={`calc-service-${service.id}`}
                  >
                    <Icon size={24} className={selectedService === service.id ? 'text-[#D4AF37]' : 'text-[#64748B]'} />
                    <span className="block font-medium">{service.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Step 2: Location */}
          <div className="mb-10">
            <div className="flex items-center gap-3 mb-4">
              <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${selectedService ? 'bg-[#D4AF37] text-[#0B0F19]' : 'bg-[#1E293B] text-[#64748B]'
                }`}>2</span>
              <h3 className="text-lg font-semibold text-[#F1F5F9] font-['DM_Sans']">
                Wo findet es statt?
              </h3>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {locationOptions.map((location) => (
                <button
                  key={location.id}
                  onClick={() => setSelectedLocation(location.id)}
                  className={`p-4 rounded-sm border transition-all ${selectedLocation === location.id
                    ? 'bg-[#D4AF37]/10 border-[#D4AF37] text-[#F1F5F9]'
                    : 'bg-[#0B0F19] border-[#1E293B] text-[#94A3B8] hover:border-[#D4AF37]/50'
                    }`}
                  data-testid={`calc-location-${location.id}`}
                >
                  <MapPin size={20} className={`mx-auto mb-2 ${selectedLocation === location.id ? 'text-[#D4AF37]' : 'text-[#64748B]'}`} />
                  <span className="block text-sm font-medium text-center">{location.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Step 3: Time */}
          <div className="mb-10">
            <div className="flex items-center gap-3 mb-4">
              <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${selectedLocation ? 'bg-[#D4AF37] text-[#0B0F19]' : 'bg-[#1E293B] text-[#64748B]'
                }`}>3</span>
              <h3 className="text-lg font-semibold text-[#F1F5F9] font-['DM_Sans']">
                Wann ungefähr?
              </h3>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {timeOptions.map((time) => (
                <button
                  key={time.id}
                  onClick={() => setSelectedTime(time.id)}
                  className={`p-4 rounded-sm border transition-all ${selectedTime === time.id
                    ? 'bg-[#D4AF37]/10 border-[#D4AF37] text-[#F1F5F9]'
                    : 'bg-[#0B0F19] border-[#1E293B] text-[#94A3B8] hover:border-[#D4AF37]/50'
                    }`}
                  data-testid={`calc-time-${time.id}`}
                >
                  <Clock size={20} className={`mx-auto mb-2 ${selectedTime === time.id ? 'text-[#D4AF37]' : 'text-[#64748B]'}`} />
                  <span className="block text-sm font-medium text-center">{time.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Step 4: Threat */}
          <div className="mb-10">
            <div className="flex items-center gap-3 mb-4">
              <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${selectedTime ? 'bg-[#D4AF37] text-[#0B0F19]' : 'bg-[#1E293B] text-[#64748B]'
                }`}>4</span>
              <h3 className="text-lg font-semibold text-[#F1F5F9] font-['DM_Sans']">
                Bedrohungslage?
              </h3>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              {threatOptions.map((threat) => {
                const Icon = threat.icon;
                return (
                  <button
                    key={threat.id}
                    onClick={() => setSelectedThreat(threat.id)}
                    className={`p-4 rounded-sm border transition-all text-left flex items-center gap-4 ${selectedThreat === threat.id
                      ? 'bg-[#D4AF37]/10 border-[#D4AF37] text-[#F1F5F9]'
                      : 'bg-[#0B0F19] border-[#1E293B] text-[#94A3B8] hover:border-[#D4AF37]/50'
                      }`}
                    data-testid={`calc-threat-${threat.id}`}
                  >
                    <Icon size={24} className={selectedThreat === threat.id ? 'text-[#D4AF37]' : 'text-[#64748B]'} />
                    <span className="block font-medium">{threat.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Step 5: Persons */}
          <div className="mb-10">
            <div className="flex items-center gap-3 mb-4">
              <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${selectedThreat ? 'bg-[#D4AF37] text-[#0B0F19]' : 'bg-[#1E293B] text-[#64748B]'
                }`}>5</span>
              <h3 className="text-lg font-semibold text-[#F1F5F9] font-['DM_Sans']">
                Für wie viele Personen?
              </h3>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              {personOptions.map((person) => {
                const Icon = person.icon;
                return (
                  <button
                    key={person.id}
                    onClick={() => setSelectedPersons(person.id)}
                    className={`p-4 rounded-sm border transition-all text-left flex items-center gap-4 ${selectedPersons === person.id
                      ? 'bg-[#D4AF37]/10 border-[#D4AF37] text-[#F1F5F9]'
                      : 'bg-[#0B0F19] border-[#1E293B] text-[#94A3B8] hover:border-[#D4AF37]/50'
                      }`}
                    data-testid={`calc-person-${person.id}`}
                  >
                    <Icon size={24} className={selectedPersons === person.id ? 'text-[#D4AF37]' : 'text-[#64748B]'} />
                    <span className="block font-medium">{person.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* CTA */}
          <div className={`p-6 rounded-sm border transition-all ${isComplete
            ? 'bg-gradient-to-r from-[#D4AF37]/20 to-[#D4AF37]/5 border-[#D4AF37]/30'
            : 'bg-[#0B0F19] border-[#1E293B]'
            }`}>
            {isComplete ? (
              <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                <div>
                  <p className="text-[#F1F5F9] font-medium mb-1">Ihre Konfiguration ist vollständig!</p>
                  <p className="text-[#94A3B8] text-sm">
                    Senden Sie uns diese Details direkt via WhatsApp.
                  </p>
                </div>
                <button
                  onClick={openWhatsApp}
                  className="btn-primary flex items-center gap-2 whitespace-nowrap"
                  data-testid="calc-whatsapp-btn"
                >
                  <MessageCircle size={18} />
                  Angebot via WhatsApp
                  <ChevronRight size={18} />
                </button>
              </div>
            ) : (
              <p className="text-[#64748B] text-center text-sm">
                Bitte wählen Sie alle Optionen aus (1-5).
              </p>
            )}
          </div>
        </motion.div>
      </div>
    </section>
  );
};
